import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import ClickSparkWrapper from '../../../components/common/ClickSparkWrapper';
import { settingsApi } from '../../../api/settingsApi';
import { voiceAgentApi, type VoiceAgentBootstrap } from '../../../api/voiceAgentApi';
import { onboardingApi } from '../../../api/onboardingApi';
import { vibeService } from '../../../services/VibeService';
import { useAppSelector } from '../../../store';
import useVoiceAgent from '../hooks/useVoiceAgent';
import { useChatScrollFade } from '../hooks/useChatScrollFade';
import FeedbackDashboard from './FeedbackDashboard';
import PracticeInterviewTypePicker, { type PracticeInterviewType } from './PracticeInterviewTypePicker';
import SystemReadinessChecks from './SystemReadinessChecks';
import { conversationHistoryToMessages } from '../utils/conversationHistoryToMessages';
import {
  DEFAULT_MOCK_INTERVIEW_TOKEN,
  getPracticeFocusTipAfterDash,
  MOCK_INTERVIEW_PRACTICE_NOTICE,
  MOCK_PRACTICE_FOCUS_AREAS,
} from '../constants';
import './AIInterviewInterface.css';
import { stripEmojis } from '../../../utils/stripEmojis';

interface Message {
  id: string;
  sender: 'ai' | 'user';
  text: string;
  timestamp: string;
}

const CONNECTING_LOADER_MESSAGES = [
  'Connecting to your interviewer…',
  'Loading your interview context…',
  'Almost ready…',
];

const FEEDBACK_LOADER_MESSAGES = [
  'Wrapping up your interview…',
  'Generating personalized feedback…',
  'Analyzing your responses…',
  'Almost ready…',
];

const ONBOARDING_LOADER_MESSAGES = [
  'Preparing your personalized interview...',
  'Reviewing your resume...',
  'Almost ready...',
];

function formatOnboardingStartError(err: unknown): string {
  const message = err instanceof Error ? err.message : 'Failed to start onboarding interview';
  if (message.toLowerCase().includes('already completed')) {
    return `${message} If you believe this is a mistake, refresh the page or contact support.`;
  }
  return message;
}

interface VoiceAgentInterviewInterfaceProps {
  isOnboarding?: boolean;
  voiceAgentBootstrap?: VoiceAgentBootstrap;
}

const VoiceAgentInterviewInterface: React.FC<VoiceAgentInterviewInterfaceProps> = ({
  isOnboarding = false,
  voiceAgentBootstrap,
}) => {
  const navigate = useNavigate();
  const user = useAppSelector((state) => state.auth.user);
  const chatMessagesEndRef = useRef<HTMLDivElement>(null);
  const chatMessagesScrollRef = useRef<HTMLDivElement>(null);
  const chatMessagesPanelRef = useRef<HTMLDivElement>(null);
  const interviewCompletedRef = useRef(false);
  const onboardingStartedRef = useRef(false);
  const cameraVideoRef = useRef<HTMLVideoElement>(null);
  const cameraStreamRef = useRef<MediaStream | null>(null);
  const bootstrapSessionRef = useRef<string | null>(null);

  const isPracticeEntry = !isOnboarding && !voiceAgentBootstrap;

  const [candidateName, setCandidateName] = useState('');
  const [selectedInterviewType, setSelectedInterviewType] = useState<PracticeInterviewType | null>(null);
  const [showStartOverlay, setShowStartOverlay] = useState(isPracticeEntry);
  const [startLoading, setStartLoading] = useState(false);
  const [onboardingLoading, setOnboardingLoading] = useState(isOnboarding);
  const [onboardingLoaderIndex, setOnboardingLoaderIndex] = useState(0);
  const [startError, setStartError] = useState<string | null>(null);
  const [showEndConfirm, setShowEndConfirm] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackData, setFeedbackData] = useState<any>(null);
  const [feedbackError, setFeedbackError] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [status, setStatus] = useState('Ready to start');
  const [connectingLoaderIndex, setConnectingLoaderIndex] = useState(0);
  const [feedbackLoaderIndex, setFeedbackLoaderIndex] = useState(0);
  const [practiceModalFocusTip, setPracticeModalFocusTip] = useState('');
  const [backendAvailable, setBackendAvailable] = useState<boolean | null>(null);
  const [cameraEnabled, setCameraEnabled] = useState(false);
  const [showContinueSpeakingPin, setShowContinueSpeakingPin] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const emailFallback = user?.email?.split('@')[0] || 'Candidate';

    onboardingApi.getUserProfile()
      .then((profileResponse) => {
        if (cancelled) return;
        const { first_name, last_name } = profileResponse.data;
        let fullName = '';
        if (first_name && last_name) {
          fullName = `${first_name} ${last_name}`.trim();
        } else if (first_name || last_name) {
          fullName = (first_name || last_name || '').trim();
        }
        setCandidateName(fullName || emailFallback);
      })
      .catch(() => {
        if (!cancelled) {
          setCandidateName(emailFallback);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [user?.email]);

  useEffect(() => {
    let cancelled = false;
    voiceAgentApi.getStatus().then((status) => {
      if (!cancelled) {
        setBackendAvailable(status?.enabled === true);
      }
    }).catch(() => {
      if (!cancelled) {
        setBackendAvailable(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const switchToLegacyInterview = useCallback(() => {
    localStorage.setItem('use_voice_agent', 'false');
    window.location.reload();
  }, []);

  const {
    connectionState,
    agentState,
    phase,
    transcript,
    sessionMetrics,
    sessionEnded,
    errorMessage,
    maxQuestions,
    audioLevels,
    start,
    connectToSession,
    endInterview,
  } = useVoiceAgent();

  const connectToSessionRef = useRef(connectToSession);
  connectToSessionRef.current = connectToSession;

  const interviewActive = connectionState === 'connected';
  const isFinalizing = phase === 'finalizing';
  const userOnTop = agentState === 'listening';
  const aiOnTop = !userOnTop;
  const aiSpeaking = agentState === 'speaking';
  const userSpeaking = agentState === 'listening';
  const effectiveFeedbackData = feedbackData ?? sessionEnded?.feedback ?? null;
  const shouldShowFeedback =
    (showFeedback || phase === 'feedback_ready') && !!effectiveFeedbackData;
  const hideMainInterview =
    shouldShowFeedback ||
    isFinalizing ||
    !!feedbackError ||
    interviewCompletedRef.current ||
    (phase === 'error' && !!sessionEnded);
  const showBlurredMain =
    !interviewActive && !isFinalizing && !shouldShowFeedback && !interviewCompletedRef.current;

  const feedbackInterviewMessages = useMemo(() => {
    if (sessionEnded?.conversation_history?.length) {
      return conversationHistoryToMessages(sessionEnded.conversation_history);
    }
    return messages;
  }, [sessionEnded?.conversation_history, messages]);

  const questionCount = sessionMetrics?.question_count ?? (interviewActive ? 1 : 0);
  const questionMax = sessionMetrics?.max_questions ?? maxQuestions;
  const warningCount = sessionMetrics?.warning_count ?? 0;
  const warningMax = sessionMetrics?.max_warnings ?? 2;
  const warningsAtLimit = warningCount >= warningMax && warningMax > 0;

  const agentStateLabel = useMemo(() => {
    if (isFinalizing) {
      return 'Generating your feedback…';
    }
    switch (agentState) {
      case 'listening':
        return 'Listening…';
      case 'thinking':
        return 'Interviewer is thinking…';
      case 'speaking':
        return 'Interviewer is speaking…';
      default:
        return interviewActive ? 'Connected' : 'Idle';
    }
  }, [agentState, interviewActive, isFinalizing]);

  useEffect(() => {
    setMessages(
      transcript.map((turn) => ({
        id: turn.id,
        sender: turn.role === 'agent' ? 'ai' : 'user',
        text: turn.text,
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      }))
    );
  }, [transcript]);

  useEffect(() => {
    chatMessagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useChatScrollFade(chatMessagesScrollRef, chatMessagesPanelRef, messages);

  useEffect(() => {
    if (isFinalizing) return;
    setStatus(agentStateLabel);
  }, [agentStateLabel, isFinalizing]);

  useEffect(() => {
    if (agentState !== 'listening') {
      setShowContinueSpeakingPin(false);
      return undefined;
    }
    const timer = window.setTimeout(() => {
      setShowContinueSpeakingPin(true);
    }, 1000);
    return () => clearTimeout(timer);
  }, [agentState]);

  useEffect(() => {
    if (!cameraEnabled) {
      if (cameraStreamRef.current) {
        cameraStreamRef.current.getTracks().forEach((track) => track.stop());
        cameraStreamRef.current = null;
      }
      if (cameraVideoRef.current) {
        cameraVideoRef.current.srcObject = null;
      }
      return undefined;
    }

    let cancelled = false;

    const startCamera = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'user', width: { ideal: 640 }, height: { ideal: 640 } },
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        cameraStreamRef.current = stream;
        if (cameraVideoRef.current) {
          cameraVideoRef.current.srcObject = stream;
        }
      } catch {
        if (!cancelled) {
          setCameraEnabled(false);
        }
      }
    };

    void startCamera();

    return () => {
      cancelled = true;
      if (cameraStreamRef.current) {
        cameraStreamRef.current.getTracks().forEach((track) => track.stop());
        cameraStreamRef.current = null;
      }
      if (cameraVideoRef.current) {
        cameraVideoRef.current.srcObject = null;
      }
    };
  }, [cameraEnabled]);

  useEffect(() => {
    if (!interviewActive && cameraEnabled) {
      setCameraEnabled(false);
    }
  }, [interviewActive, cameraEnabled]);

  useEffect(() => {
    if (!startLoading) return undefined;
    const timer = window.setInterval(() => {
      setConnectingLoaderIndex((i) => (i + 1) % CONNECTING_LOADER_MESSAGES.length);
    }, 2200);
    return () => clearInterval(timer);
  }, [startLoading]);

  useEffect(() => {
    if (!isFinalizing) return undefined;
    setStatus('Generating your feedback…');
    const timer = window.setInterval(() => {
      setFeedbackLoaderIndex((i) => (i + 1) % FEEDBACK_LOADER_MESSAGES.length);
    }, 2200);
    return () => clearInterval(timer);
  }, [isFinalizing]);

  useEffect(() => {
    if (!sessionEnded) return;

    setShowStartOverlay(false);
    setFeedbackError(null);

    if (sessionEnded.message) {
      setMessages((prev) => {
        const last = prev[prev.length - 1];
        if (last?.sender === 'ai' && last.text === sessionEnded.message) {
          return prev;
        }
        return [
          ...prev,
          {
            id: `end-${Date.now()}`,
            sender: 'ai' as const,
            text: sessionEnded.message!,
            timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          },
        ];
      });
    }

    if (sessionEnded.feedback) {
      interviewCompletedRef.current = true;
      setFeedbackData(sessionEnded.feedback);
      setShowFeedback(true);
      setFeedbackError(null);
      vibeService.trigger('celebration');
      const score = sessionEnded.feedback.overall_score;
      const readiness = sessionEnded.feedback.readiness_level;
      if (score != null && readiness) {
        setStatus(`Interview complete — ${score}/100 (${readiness})`);
      } else {
        setStatus('Interview completed! Review your feedback below.');
      }
    } else if (sessionEnded.reason === 'user_request') {
      setFeedbackError(null);
      setStatus('Interview ended');
    } else if (sessionEnded.reason === 'max_warnings') {
      interviewCompletedRef.current = true;
      setFeedbackError(null);
      setStatus('Session ended — too many warnings');
      navigate('/candidate');
    } else {
      setFeedbackError(
        'Feedback is taking longer than expected. You can view it from your Candidate Profile shortly.'
      );
      setStatus(`Session ended: ${sessionEnded.reason}`);
    }
  }, [sessionEnded]);

  useEffect(() => {
    if (errorMessage && phase === 'error' && !sessionEnded?.feedback) {
      setFeedbackError(errorMessage);
    }
  }, [errorMessage, phase, sessionEnded?.feedback]);

  useEffect(() => {
    if (errorMessage && !isFinalizing && !sessionEnded) {
      setStartError(errorMessage);
      setStartLoading(false);
      vibeService.trigger('error');
    }
  }, [errorMessage, isFinalizing, sessionEnded]);

  useEffect(() => {
    if (!onboardingLoading) return undefined;
    const timer = window.setInterval(() => {
      setOnboardingLoaderIndex((i) => (i + 1) % ONBOARDING_LOADER_MESSAGES.length);
    }, 2200);
    return () => clearInterval(timer);
  }, [onboardingLoading]);

  useEffect(() => {
    if (!isOnboarding) return;
    if (connectionState === 'connected' || phase === 'active') {
      setOnboardingLoading(false);
    }
  }, [connectionState, isOnboarding, phase]);

  const loadInterviewSettings = useCallback(async () => {
    let difficulty = 'medium';
    let experienceFocus = 'varied';
    let mode = 'professional';

    try {
      const settingsResponse = await settingsApi.getInterviewSettings();
      if (settingsResponse.success && settingsResponse.data?.settings) {
        difficulty = settingsResponse.data.settings.difficulty;
        experienceFocus = settingsResponse.data.settings.experience_focus;
        mode = settingsResponse.data.settings.mode;
      }
    } catch {
      // fall back to defaults
    }

    return { difficulty, experienceFocus, mode };
  }, []);

  const handleBeginInterview = useCallback(async () => {
    if (isPracticeEntry && selectedInterviewType !== 'mock_ai') {
      setStartError('Please select Mock Interview to continue');
      return;
    }

    setStartError(null);
    setFeedbackError(null);
    setStartLoading(true);
    vibeService.trigger('loading');

    const focusPick =
      MOCK_PRACTICE_FOCUS_AREAS[Math.floor(Math.random() * MOCK_PRACTICE_FOCUS_AREAS.length)] ?? '';
    setPracticeModalFocusTip(getPracticeFocusTipAfterDash(focusPick));

    try {
      const { difficulty, experienceFocus, mode } = await loadInterviewSettings();

      await start({
        context_type: 'practice',
        interview_type: 'general',
        candidate_name: candidateName || undefined,
        difficulty,
        experience_focus: experienceFocus,
        mode,
        token: DEFAULT_MOCK_INTERVIEW_TOKEN,
      });

      setShowStartOverlay(false);
      setStartLoading(false);
      vibeService.dismiss();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to start interview';
      setStartError(message);
      setStartLoading(false);
      vibeService.trigger('error');
    }
  }, [candidateName, isPracticeEntry, loadInterviewSettings, selectedInterviewType, start]);

  const handleTypeSelect = useCallback((type: PracticeInterviewType) => {
    setSelectedInterviewType(type);
    if (startError) setStartError(null);

    if (type === 'coding') {
      navigate('/coding_challenge');
      return;
    }

    if (type === 'group_discussion') {
      navigate('/group-discussion', {
        state: { token: DEFAULT_MOCK_INTERVIEW_TOKEN },
      });
    }
  }, [navigate, startError]);

  useEffect(() => {
    if (!voiceAgentBootstrap || backendAvailable !== true) return;

    const sessionKey = voiceAgentBootstrap.sessionId;
    if (bootstrapSessionRef.current === sessionKey) return;
    bootstrapSessionRef.current = sessionKey;

    let cancelled = false;
    const connectBootstrap = async () => {
      setShowStartOverlay(false);
      setOnboardingLoading(false);
      setStartLoading(true);
      setStartError(null);
      vibeService.trigger('loading');

      try {
        await connectToSessionRef.current({
          sessionId: voiceAgentBootstrap.sessionId,
          wsPath: voiceAgentBootstrap.wsPath,
          firstQuestion: voiceAgentBootstrap.firstQuestion,
          maxQuestions: voiceAgentBootstrap.maxQuestions,
        });
        if (!cancelled) {
          setStartLoading(false);
          vibeService.dismiss();
        }
      } catch (err: unknown) {
        if (!cancelled) {
          const message = err instanceof Error ? err.message : 'Failed to connect to interview';
          setStartError(message);
          setStartLoading(false);
          vibeService.trigger('error');
        }
      }
    };

    void connectBootstrap();
    return () => {
      cancelled = true;
    };
  }, [backendAvailable, voiceAgentBootstrap]);

  useEffect(() => {
    if (!isOnboarding || backendAvailable !== true) return;
    if (onboardingStartedRef.current) return;

    setShowStartOverlay(false);
    setOnboardingLoading(true);

    let cancelled = false;
    const startOnboarding = async () => {
      if (onboardingStartedRef.current) return;
      onboardingStartedRef.current = true;

      try {
        const { difficulty, experienceFocus, mode } = await loadInterviewSettings();
        const nameFallback = user?.email?.split('@')[0];
        await start({
          context_type: 'onboarding',
          interview_type: 'onboarding',
          candidate_name: candidateName || nameFallback || undefined,
          difficulty,
          experience_focus: experienceFocus,
          mode,
        });
        setOnboardingLoading(false);
      } catch (err: unknown) {
        if (!cancelled) {
          setStartError(formatOnboardingStartError(err));
          setOnboardingLoading(false);
          vibeService.trigger('error');
        }
      }
    };

    const timer = window.setTimeout(() => {
      void startOnboarding();
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [backendAvailable, isOnboarding, loadInterviewSettings, start, user?.email]);

  const handleConfirmEnd = useCallback(async () => {
    setShowEndConfirm(false);
    interviewCompletedRef.current = true;
    setStatus('Ending interview…');
    await endInterview();
    navigate('/candidate');
  }, [endInterview, navigate]);

  return (
    <div className="ai-interview-interface">
      {shouldShowFeedback && effectiveFeedbackData && (
        <div className="voice-agent-feedback-overlay">
          <FeedbackDashboard
            feedbackData={effectiveFeedbackData}
            candidateName={candidateName}
            interviewMessages={feedbackInterviewMessages}
            conversationHistory={sessionEnded?.conversation_history}
            onClose={() => {
              interviewCompletedRef.current = true;
              setShowFeedback(false);
              setFeedbackData(null);
              navigate('/candidate');
            }}
          />
        </div>
      )}

      {isFinalizing && !shouldShowFeedback && (
        <div
          className="mock-interview-loading-modal-backdrop"
          role="dialog"
          aria-modal="true"
          aria-busy="true"
          aria-labelledby="voice-agent-feedback-loading-title"
        >
          <div className="mock-interview-loading-modal">
            <h2 id="voice-agent-feedback-loading-title" className="mock-interview-loading-modal__title">
              Preparing your feedback
            </h2>
            <p className="mock-interview-loading-modal__notice">
              Your interview responses are being analyzed. This usually takes under a minute.
            </p>
            <div className="interview-connecting-loader mock-interview-loading-modal__loader">
              <span className="interview-connecting-loader__spinner" aria-hidden="true" />
              <p className="interview-connecting-loader__text" key={feedbackLoaderIndex}>
                {FEEDBACK_LOADER_MESSAGES[feedbackLoaderIndex]}
              </p>
            </div>
          </div>
        </div>
      )}

      {feedbackError && !shouldShowFeedback && !isFinalizing && (
        <div
          className="mock-interview-loading-modal-backdrop"
          role="dialog"
          aria-modal="true"
        >
          <div className="mock-interview-loading-modal">
            <h2 className="mock-interview-loading-modal__title">Feedback unavailable</h2>
            <p className="mock-interview-loading-modal__notice">{feedbackError}</p>
            <div className="start-button-container" style={{ marginTop: 24 }}>
              <button
                type="button"
                className="start-interview-button"
                onClick={() => navigate('/candidate')}
              >
                Go to Candidate Profile
              </button>
            </div>
          </div>
        </div>
      )}

      {showEndConfirm && (
        <div className="end-confirm-overlay">
          <div className="end-confirm-widget">
            <h2 className="end-confirm-title">End interview?</h2>
            <p className="end-confirm-text">
              Are you sure you want to end the interview mid way?
            </p>
            <div className="end-confirm-actions">
              <button type="button" className="end-confirm-btn" onClick={() => setShowEndConfirm(false)}>
                no
              </button>
              <button type="button" className="end-confirm-btn danger" onClick={handleConfirmEnd}>
                yes
              </button>
            </div>
          </div>
        </div>
      )}

      {onboardingLoading && (
        <div
          className="mock-interview-loading-modal-backdrop"
          role="dialog"
          aria-modal="true"
          aria-busy="true"
        >
          <div className="mock-interview-loading-modal">
            <h2 className="mock-interview-loading-modal__title">Preparing Your Interview</h2>
            <p className="mock-interview-loading-modal__notice">
              Setting up your onboarding voice interview.
            </p>
            <div className="interview-connecting-loader mock-interview-loading-modal__loader">
              <span className="interview-connecting-loader__spinner" aria-hidden="true" />
              <p className="interview-connecting-loader__text" key={onboardingLoaderIndex}>
                {ONBOARDING_LOADER_MESSAGES[onboardingLoaderIndex]}
              </p>
            </div>
          </div>
        </div>
      )}

      {startLoading && backendAvailable !== false && !interviewActive && !shouldShowFeedback && !onboardingLoading && (
        <div
          className="mock-interview-loading-modal-backdrop"
          role="dialog"
          aria-modal="true"
          aria-busy="true"
        >
          <div className="mock-interview-loading-modal">
            <h2 className="mock-interview-loading-modal__title">Preparing your session</h2>
            <p className="mock-interview-loading-modal__notice">
              {voiceAgentBootstrap
                ? 'Connecting to your assigned interview round...'
                : MOCK_INTERVIEW_PRACTICE_NOTICE}
            </p>
            {practiceModalFocusTip ? (
              <p className="mock-interview-loading-modal__focus-tip">{practiceModalFocusTip}</p>
            ) : null}
            <div className="interview-connecting-loader mock-interview-loading-modal__loader">
              <span className="interview-connecting-loader__spinner" aria-hidden="true" />
              <p className="interview-connecting-loader__text" key={connectingLoaderIndex}>
                {CONNECTING_LOADER_MESSAGES[connectingLoaderIndex]}
              </p>
            </div>
          </div>
        </div>
      )}

      {backendAvailable === false && (
        <div className="start-interview-overlay">
          <div className="start-interview-widget">
            <div className="widget-header">
              <h1 className="widget-title">Voice agent unavailable</h1>
              <p className="widget-subtitle">
                The low-latency voice interview is not enabled on this server.
              </p>
            </div>
            <div className="widget-content">
              <p className="knob-description" style={{ marginBottom: 16 }}>
                You can continue with the standard mock interview engine instead.
              </p>
              <div className="start-button-container">
                <button type="button" className="start-interview-button" onClick={switchToLegacyInterview}>
                  Use standard interview
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showStartOverlay && backendAvailable !== false && !interviewActive && !shouldShowFeedback && !startLoading && !isFinalizing && !feedbackError && !onboardingLoading && isPracticeEntry && (
        <div className="start-interview-overlay">
          <div className="start-interview-widget">
            <div className="widget-header">
              <button
                className="back-button"
                onClick={() => window.history.back()}
                title="Go back"
                type="button"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M20 11H7.83L13.42 5.41L12 4L4 12L12 20L13.41 18.59L7.83 13H20V11Z" fill="currentColor" />
                </svg>
              </button>
              <div className="widget-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2C13.1 2 14 2.9 14 4C14 5.1 13.1 6 12 6C10.9 6 10 5.1 10 4C10 2.9 10.9 2 12 2ZM21 9V7L15 1H5C3.89 1 3 1.89 3 3V21C3 22.11 3.89 23 5 23H11V21H5V3H13V9H21Z" fill="currentColor" />
                  <path d="M16 10V12H18V10H20V12H22V14H20V16H18V14H16V12H14V10H16Z" fill="currentColor" />
                </svg>
              </div>
              <h1 className="widget-title">Practice Interview with Andrea</h1>
              <p className="widget-subtitle">
                Professional interview simulation powered by advanced AI assessment
              </p>
            </div>

            <div className="widget-content">
              <SystemReadinessChecks />

              <PracticeInterviewTypePicker
                selectedType={selectedInterviewType}
                onSelect={handleTypeSelect}
                onSettingsClick={() => navigate('/settings')}
              />

              {startError && (
                <div className="token-error-message">{startError}</div>
              )}

              {selectedInterviewType === 'mock_ai' && (
                <div className="start-button-container">
                  <ClickSparkWrapper>
                    <button type="button" className="start-interview-button" onClick={handleBeginInterview}>
                      <span className="start-button-inner">
                        <span>Begin</span>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                          <path d="M8 5V19L19 12L8 5Z" fill="currentColor" />
                        </svg>
                      </span>
                    </button>
                  </ClickSparkWrapper>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {(startError && !isPracticeEntry && !shouldShowFeedback) && (
        <div
          className="mock-interview-loading-modal-backdrop"
          role="dialog"
          aria-modal="true"
        >
          <div className="mock-interview-loading-modal">
            <h2 className="mock-interview-loading-modal__title">Could not start interview</h2>
            <p className="mock-interview-loading-modal__notice">{startError}</p>
            <div className="start-button-container" style={{ marginTop: 24 }}>
              <button
                type="button"
                className="start-interview-button"
                onClick={() => navigate('/candidate')}
              >
                Go to Candidate Profile
              </button>
            </div>
          </div>
        </div>
      )}

      {!hideMainInterview && (
      <div className={`main-container ${showBlurredMain ? 'blurred' : ''}`}>
        <div className="video-section">
          <div className="header-bar">
            <div className="header-left">
              <div className="record-indicator" />
              <img src="/image_i_do1.png" alt="Luci logo" className="session-logo" />
              <div className="participants-count">2 Participants</div>
            </div>
            <div className="header-right">
              <div className={`warning-counter${warningsAtLimit ? ' warning-counter--limit' : ''}`}>
                Warnings {warningCount} of {warningMax}
              </div>
            </div>
          </div>

          <div className="va-overlap-stage" aria-label="Interview participants">
            <div
              className={`va-circle va-circle-ai${aiOnTop ? ' is-front' : ''}${aiSpeaking ? ' is-speaking' : ''}`}
            >
              <div className="va-circle-inner">
                <div className="va-name">Andrea</div>
                <div className="va-subtext">Interviewer</div>
              </div>
            </div>
            <div
              className={`va-circle va-circle-user${userOnTop ? ' is-front' : ''}${userSpeaking ? ' is-speaking' : ''}${cameraEnabled ? ' has-video' : ''}`}
            >
              <video
                ref={cameraVideoRef}
                className="va-camera-video"
                autoPlay
                playsInline
                muted
                hidden={!cameraEnabled}
              />
              <div className={`va-circle-inner${cameraEnabled ? ' va-circle-inner-overlay' : ''}`}>
                <div className="va-name">{candidateName || 'You'}</div>
                <div className="va-subtext">You</div>
              </div>
            </div>
          </div>

          {interviewActive && !isFinalizing && (
            <div className="controls-hint-fixed">
              Speak when the interviewer finishes — your turn is detected automatically.
            </div>
          )}

          <div className="controls-bar">
            <div
              className={`va-mic-indicator ${agentState === 'listening' ? 'can-speak' : 'dont-speak'}`}
              role="status"
              aria-live="polite"
              title={agentState === 'listening' ? 'Your turn — speak now' : 'Wait for the interviewer'}
            >
              {showContinueSpeakingPin && (
                <div className="va-mic-speak-pin">Continue speaking</div>
              )}
              <div className="va-mic-waveform">
                {audioLevels.map((level, i) => (
                  <div
                    key={i}
                    className="va-mic-wave-bar"
                    style={{ height: `${Math.max(level * 100, 12)}%` }}
                  />
                ))}
              </div>
            </div>

            <button
              type="button"
              className={`control-btn secondary ${cameraEnabled ? 'active' : ''}`}
              title={cameraEnabled ? 'Turn camera off' : 'Turn camera on'}
              onClick={() => setCameraEnabled((prev) => !prev)}
              disabled={!interviewActive || isFinalizing}
            >
              {cameraEnabled ? (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M23 7l-7 5 7 5V7z" />
                  <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
                </svg>
              ) : (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M16 16v1a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h2m5.66 0H14a2 2 0 0 1 2 2v3.34l1 1L23 7v10" />
                  <line x1="1" y1="1" x2="23" y2="23" />
                </svg>
              )}
            </button>

            <button
              type="button"
              className="control-btn danger"
              title="End Interview"
              onClick={() => setShowEndConfirm(true)}
              disabled={!interviewActive || isFinalizing}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                <line x1="23" y1="1" x2="17" y2="7" />
                <line x1="17" y1="1" x2="23" y2="7" />
              </svg>
            </button>
          </div>
        </div>

        <div className="chat-sidebar">
          <div className="chat-header">
            <div className="chat-title">Interview Chat</div>
            <div className="question-counter">
              Question {questionCount} of {questionMax}
            </div>
          </div>

          {agentState === 'thinking' && !isFinalizing && (
            <div className="processing-indicator">
              <div className="processing-spinner" />
              <div className="processing-text">
                Interviewer is thinking<span className="processing-dots" />
              </div>
            </div>
          )}

          {isFinalizing && (
            <div className="processing-indicator">
              <div className="processing-spinner" />
              <div className="processing-text">
                Generating feedback<span className="processing-dots" />
              </div>
            </div>
          )}

          <div className="chat-messages-panel" ref={chatMessagesPanelRef}>
          <div className="chat-messages" ref={chatMessagesScrollRef}>
            {messages.map((message, index) => (
              <div
                key={message.id}
                className={`message ${message.sender} ${
                  index === messages.length - 1 ? 'message-recent' : 'message-past'
                }`}
              >
                <div className="message-bubble">
                  {message.sender === 'ai' ? (
                    <ReactMarkdown>{message.text}</ReactMarkdown>
                  ) : (
                    message.text
                  )}
                </div>
                <div className="message-time">{message.timestamp}</div>
              </div>
            ))}
            <div ref={chatMessagesEndRef} />
          </div>
          </div>

          <div className="status-bar">
            <div className="status-dot" />
            <div>{stripEmojis(status)}</div>
          </div>
        </div>
      </div>
      )}
    </div>
  );
};

export default VoiceAgentInterviewInterface;
